import { IconName } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getCurrentEnterpriseChainId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    isCurrentProfileEnabledToManageCustomers,
    isCurrentProfileEnabledToManageCompanies,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { showRouteInModal } from '../../../app/actions/common';
import { AssistantSearchListItemButton } from '../results/searchListItemButton';
import { StyledLeftIcon } from '../styles';
import { KeyboardShortcut } from '../KeyboardShortcut';
import { NEW_RESERVATION_BUTTON_DATA_TEST_ID } from '../../../newReservation/constants';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface CommandListProps extends AssistantSearchListItemProps {
    'data-test-command-type'?: string;
}

export const useCommandList = (searchTerm: string): CommandListProps[] => {
    const enterpriseId = useCurrentEnterpriseId();
    const isCompaniesManager = useCommanderSelector<boolean>(isCurrentProfileEnabledToManageCompanies);
    const chainId = useCommanderSelector<string>(getCurrentEnterpriseChainId);

    const isCustomerManager = useCommanderSelector<boolean>(isCurrentProfileEnabledToManageCustomers);
    const plusIcon = <StyledLeftIcon size="small" value={IconName.PlusSimple} />;

    const list: Array<CommandListProps> = [];
    if (isCompaniesManager) {
        list.push({
            itemId: AssistantSearchListItemButton.NewCompany,
            'data-test-command-type': 'newCompany',
            primaryText: <LocalizedText textKey={TextKey.PartnerCompanyNew} />,
            rightItem: <KeyboardShortcut keyboardShortcut={['Alt', 'C']} />,
            leftIcon: plusIcon,
            routeAction: showRouteInModal(RouteName.CompanyCreate, {
                query: {
                    ownerId: chainId,
                    name: searchTerm,
                },
            }),
        });
    }

    if (isCustomerManager) {
        list.push({
            itemId: AssistantSearchListItemButton.NewCustomer,
            'data-test-command-type': 'newCustomer',
            primaryText: <LocalizedText textKey={TextKey.CustomerNew} />,
            rightItem: <KeyboardShortcut keyboardShortcut={['Alt', 'G']} />,
            leftIcon: plusIcon,
            routeAction: showRouteInModal(RouteName.CustomerCreate, {
                query: {
                    RedirectToProfile: true,
                    ChainId: chainId,
                    LastName: searchTerm,
                },
            }),
        });
    }

    list.push({
        itemId: AssistantSearchListItemButton.NewReservation,
        'data-test-command-type': 'newReservation',
        'data-test-id': NEW_RESERVATION_BUTTON_DATA_TEST_ID,
        primaryText: <LocalizedText textKey={TextKey.ReservationNew} />,
        rightItem: <KeyboardShortcut keyboardShortcut={['Alt', 'N']} />,
        leftIcon: plusIcon,
        routeAction: showRouteInModal(RouteName.ReservationCreate, { query: { HotelId: enterpriseId } }),
    });

    return list;
};
